import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  // DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Divider
} from '@mui/material';
import {
  FitnessCenter as FitnessCenterIcon,
  EmojiEvents as EmojiEventsIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon
} from '@mui/icons-material';
// import CustomPieChart from '../../../components/visualisation/Pie Chart';
import LinearProgressWithLabel from '../../../components/visualisation/LinearProgressWithPercent';

interface LiveSessionStatsProps {
  entries: any[];
  onClose: () => void;
  discipline?: string;
}

const LiveSessionStats: React.FC<LiveSessionStatsProps> = ({ 
  entries, 
  onClose, 
  discipline = 'trampoline' 
}) => {
  
  // Calculate totals from entries
  const calculateStats = () => {
    const stats = {
      totalExercises: 0,
      totalSkills: 0,
      totalDifficulty: 0,
      totalLoad: 0,
      averageTof: 0,
      averageStopwatch: 0,
      exerciseSuccessRate: 0,
      skillsSuccessRate: 0,
      totalRoutines: 0,
      totalDrills: 0,
      totalSkillAttempts: 0
    };

    if (!entries || entries.length === 0) return stats;

    let tofSum = 0;
    let tofCount = 0;
    let stopwatchSum = 0;
    let stopwatchCount = 0;
    let completedExercises = 0;
    let totalSkillsAttempted = 0;

    entries.forEach(entry => {
      const reps = entry.repetitions || 1;
      
      // Basic totals
      stats.totalExercises += reps;
      stats.totalSkills += (entry.total_completed_skills || 0) * reps;
      stats.totalDifficulty += (entry.total_difficulty || 0) * reps;
      stats.totalLoad += entry.total_load || 0;

      // Exercise completion
      if (entry.completed) {
        completedExercises += reps;
      }

      // Skills attempted vs completed for skill success rate
      totalSkillsAttempted += (entry.total_skills || 0) * reps;

      // Time calculations (only for non-tumbling disciplines)
      if (discipline !== 'tumbling') {
        if (entry.total_tof > 0 && entry.total_completed_skills > 0) {
          const avgTimePerSkill = entry.total_tof / entry.total_completed_skills;
          
          if (entry.time_method === 'tof' || entry.timing_method === 'tof') {
            tofSum += avgTimePerSkill;
            tofCount++;
          } else if (entry.time_method === 'stopwatch' || entry.timing_method === 'stopwatch') {
            stopwatchSum += avgTimePerSkill;
            stopwatchCount++;
          }
        }
      }

      // Exercise type categorization
      const type = entry.objective?.type || entry.type;
      switch (type) {
        case 'routine':
        case 'pass':
          stats.totalRoutines += reps;
          break;
        case 'drill':
        case 'section':
          stats.totalDrills += reps;
          break;
        case 'skill':
        case 'new_skill':
        case 'new-skill':
        case 'mount':
        case 'spotter':
        case 'dismount':
          stats.totalSkillAttempts += reps;
          break;
      }
    });

    // Calculate averages and rates
    stats.averageTof = tofCount > 0 ? tofSum / tofCount : 0;
    stats.averageStopwatch = stopwatchCount > 0 ? stopwatchSum / stopwatchCount : 0;
    stats.exerciseSuccessRate = stats.totalExercises > 0 
      ? (completedExercises / stats.totalExercises) * 100 
      : 0;
    stats.skillsSuccessRate = totalSkillsAttempted > 0 
      ? (stats.totalSkills / totalSkillsAttempted) * 100 
      : 0;

    return stats;
  };

  const stats = calculateStats();

  const StatCard = ({ icon, title, value, subtitle }: {
    icon: React.ReactNode;
    title: string;
    value: string | number;
    subtitle?: string;
  }) => (
    <Paper elevation={1} sx={{ p: 2, textAlign: 'center', height: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: 'primary.main' }}>
          {icon}
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          {typeof value === 'number' && value % 1 !== 0 ? value.toFixed(2) : value}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Paper>
  );

  const ProgressCard = ({ title, value }: { title: string; value: number }) => (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1, textAlign: 'center' }}>
        {title}: {value.toFixed(1)}%
      </Typography>
      <LinearProgressWithLabel progress={Math.round(value)} />
    </Paper>
  );

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Live Session Stats
            </Typography>
          </Box>
          <Button
            onClick={onClose}
            sx={{ minWidth: 'auto', p: 1 }}
            color="inherit"
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Box sx={{ space: 3 }}>
          {/* Main Stats Grid */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4}>
              <StatCard
                icon={<FitnessCenterIcon />}
                title="Total Exercises"
                value={stats.totalExercises}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard
                icon={<EmojiEventsIcon />}
                title="Skills Completed"
                value={stats.totalSkills}
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard
                icon={<TrendingUpIcon />}
                title="Total Difficulty"
                value={stats.totalDifficulty}
              />
            </Grid>
            {/* {stats.totalLoad > 0 && (
              <Grid item xs={6} sm={4}>
                <StatCard
                  icon={<TrendingUpIcon />}
                  title="Total Load"
                  value={stats.totalLoad}
                />
              </Grid>
            )} */}
            {discipline !== 'tumbling' && stats.averageTof > 0 && (
              <Grid item xs={6} sm={4}>
                <StatCard
                  icon={<AccessTimeIcon />}
                  title="Average ToF"
                  value={`${stats.averageTof.toFixed(2)}s`}
                />
              </Grid>
            )}
            {discipline !== 'tumbling' && stats.averageStopwatch > 0 && (
              <Grid item xs={6} sm={4}>
                <StatCard
                  icon={<AccessTimeIcon />}
                  title="Average Stopwatch"
                  value={`${stats.averageStopwatch.toFixed(2)}s`}
                />
              </Grid>
            )}
          </Grid>

          {/* Success Rates */}
          {(stats.exerciseSuccessRate > 0 || stats.skillsSuccessRate > 0) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Success Rates
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {stats.exerciseSuccessRate > 0 && (
                  <Grid item xs={12} sm={6}>
                    <ProgressCard
                      title="Exercise Completion"
                      value={stats.exerciseSuccessRate}
                    />
                  </Grid>
                )}
                {stats.skillsSuccessRate > 0 && (
                  <Grid item xs={12} sm={6}>
                    <ProgressCard
                      title="Skills Completion"
                      value={stats.skillsSuccessRate}
                    />
                  </Grid>
                )}
              </Grid>
            </>
          )}

          {/* Exercise Breakdown Pie Chart */}
          {/* {(stats.totalRoutines > 0 || stats.totalDrills > 0 || stats.totalSkillAttempts > 0) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, textAlign: 'center' }}>
                Exercise Breakdown
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <CustomPieChart data={[
                  { name: "Routines", value: stats.totalRoutines },
                  { name: "Drills", value: stats.totalDrills },
                  { name: "Skills", value: stats.totalSkillAttempts },
                  { 
                    name: "Other", 
                    value: stats.totalExercises - (stats.totalRoutines + stats.totalDrills + stats.totalSkillAttempts) 
                  },
                ].filter(item => item.value > 0)} />
              </Box>
            </>
          )} */}

          {/* No data message */}
          {entries.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No entries recorded yet. Start adding exercises to see live stats!
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{ minWidth: 100 }}
        >
          Close
        </Button>
      </DialogActions> */}
    </Dialog>
  );
};

export default LiveSessionStats;